import { useMutation } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';

import { toast } from 'sonner';
import { toNoonUTC } from '@/shared/lib/utils';
import { partnerApi } from '@client/api/waterlily';
import { selectSessionAdvisor } from '@client/model/selectors';
import { fetchClientByClientIdRequest } from '@client/model/clientModel';
import { fetchClientsForAdvisorByAdvisorIdRequest } from '@client/model/clientsModel';

import type { AddPartnerFormValues } from '@/features/add-partner';

interface UseAddPartnerOptions {
  clientId: string;
  onSuccess?: (data: PostAddPartnerResponse) => void;
}

function mapFormToRequest(formValues: AddPartnerFormValues): PostAddPartnerRequest {
  const genderMap: Record<string, string> = {
    male: 'Male',
    female: 'Female',
  };

  const smokerMap: Record<string, string> = {
    never: 'No',
    former: 'Yes',
    current: 'Yes',
  };

  return {
    partnerFirstName: formValues.firstName || undefined,
    partnerLastName: formValues.lastName || undefined,
    partnerBirthDate: toNoonUTC(formValues.birthDate),
    partnerGenderAtBirth: genderMap[formValues.sex] ?? 'Male',
    partnerSmokerEver: smokerMap[formValues.smokingStatus] ?? 'No',
    partnerZipCode: formValues.zipCode,
    partnerEmail: formValues.email,
    sendInviteNow: formValues.sendInviteNow,
  };
}

export function useAddPartner({ clientId, onSuccess }: UseAddPartnerOptions) {
  const dispatch = useDispatch();
  const advisor = useSelector(selectSessionAdvisor);

  const mutation = useMutation({
    mutationFn: (formValues: AddPartnerFormValues) => {
      const request = mapFormToRequest(formValues);
      return partnerApi.addPartner(clientId, request);
    },
    onSuccess: data => {
      dispatch(fetchClientByClientIdRequest(clientId));
      if (advisor?.advisorId) {
        dispatch(
          fetchClientsForAdvisorByAdvisorIdRequest({
            advisorId: advisor.advisorId,
            isInvalidation: true,
          }),
        );
      }
      toast.success('Partner added successfully');
      onSuccess?.(data);
    },
    onError: (error: unknown) => {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to add partner. Please try again.';
      toast.error(errorMessage);
    },
  });

  return {
    addPartner: mutation.mutate,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    reset: mutation.reset,
  };
}
